import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { intlLocaleFor } from "../../../../i18n/languages";
import {
  updateReportVariable as updateReportVariableApi,
  createReportVariableMeasurement as createReportVariableMeasurementApi,
  fetchReportVariableMeasurements,
  deleteReportVariable as deleteReportVariableApi,
} from "../../../../api/service/report-variables";
import {
  getFlowRateUnitKind,
  formatUnitDisplay,
} from "../../../../utils/reportVariableUnits";
import {
  RV_DIALOG_Z,
  RV_OVERLAY_CLASS,
  RV_CENTER_WRAPPER,
  RV_CENTER_INNER,
  rvTransitionOverlay,
  rvTransitionPanel,
  RV_INPUT,
  RV_INPUT_MEASURED,
  RV_BTN_OUTLINE_SM,
  RV_BTN_OUTLINE,
  RV_BTN_PRIMARY_DARK,
  RV_CLOSE_BTN,
} from "./report-variable-dialog-theme";

/** Interpret display input as m³/h or l/s and return equivalent m³/h. */
function displayInputToM3h(raw, displayKind) {
  const n = parseFloat(String(raw ?? "").replace(",", "."));
  if (raw == null || String(raw).trim() === "" || Number.isNaN(n)) return null;
  return displayKind === "m3h" ? n : n * 3.6;
}

/** m³/h to current display string */
function m3hToDisplay(m3h, displayKind) {
  if (m3h == null || Number.isNaN(m3h)) return "";
  const v = displayKind === "m3h" ? m3h : m3h / 3.6;
  return String(Math.round(v * 100) / 100);
}

/** Stored API value → m³/h (for flow units only) */
function storedToM3h(value, storageKind) {
  const n = parseFloat(value);
  if (value == null || String(value).trim() === "" || Number.isNaN(n)) return null;
  if (storageKind === "m3h") return n;
  if (storageKind === "ls") return n * 3.6;
  return n;
}

/** m³/h → stored value for API */
function m3hToStored(m3h, storageKind) {
  if (m3h == null || Number.isNaN(m3h)) return null;
  if (storageKind === "m3h") return m3h;
  if (storageKind === "ls") return m3h / 3.6;
  return m3h;
}

function formatMeasurementDate(dateString, locale = "nb-NO") {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleString(locale, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const VariableSketchEditorDialog = ({
  open,
  onClose,
  variable,
  typeGroup,
  group,
  report,
  canEdit,
  mutations,
  collaborationAnchor,
  onSaved,
}) => {
  const { t, i18n } = useTranslation();
  const updateReportVariable = mutations?.updateReportVariable || updateReportVariableApi;
  const createReportVariableMeasurement = mutations?.createReportVariableMeasurement || createReportVariableMeasurementApi;
  const deleteReportVariable = mutations?.deleteReportVariable || deleteReportVariableApi;

  const [activeTab, setActiveTab] = useState("measurements");
  const [displayKind, setDisplayKind] = useState("m3h");
  const [reference, setReference] = useState("");
  const [projInput, setProjInput] = useState("");
  const [preInput, setPreInput] = useState("");
  const [measInput, setMeasInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [measurements, setMeasurements] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isAddingMeasurement, setIsAddingMeasurement] = useState(false);
  const [newMeasurementInput, setNewMeasurementInput] = useState("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  /** Sub-variables in lists often omit `is_main_variable: false` explicitly — treat anything not main as deletable here. */
  const isSubVariable = variable != null && variable.is_main_variable !== true;

  useEffect(() => {
    if (!open) {
      setDeleteConfirmOpen(false);
      setDeleting(false);
    }
  }, [open]);

  const measurementUnit = typeGroup?.measurement_unit;
  const projectedUnit = typeGroup?.projected_unit || typeGroup?.measurement_unit;
  const projKind = getFlowRateUnitKind(projectedUnit);
  const measKind = getFlowRateUnitKind(measurementUnit);

  /** m³/h ↔ l/s only when both relevant type-group units are flow (same normalization for all fields). */
  const showFlowToggle = !!(projKind && measKind);

  const projectedUnitShort =
    projectedUnit?.short_title || projectedUnit?.title || projectedUnit?.displayTitle || "";
  const measurementUnitShort =
    measurementUnit?.short_title ||
    measurementUnit?.title ||
    measurementUnit?.displayTitle ||
    "";

  const projectedFieldSuffix = projKind
    ? displayKind === "m3h"
      ? "m³/h"
      : "l/s"
    : projectedUnitShort;

  const measurementFieldSuffix = measKind
    ? displayKind === "m3h"
      ? "m³/h"
      : "l/s"
    : measurementUnitShort;

  const parseNumericInput = (raw) => {
    const n = parseFloat(String(raw ?? "").replace(",", "."));
    return Number.isNaN(n) ? null : n;
  };

  const syncMeasInputFromStored = useCallback(
    (storedValue) => {
      if (storedValue == null || String(storedValue).trim() === "") {
        setMeasInput("");
        return;
      }
      if (measKind) {
        const m3 = storedToM3h(storedValue, measKind);
        setMeasInput(m3 != null ? m3hToDisplay(m3, displayKind) : String(storedValue));
      } else {
        setMeasInput(String(storedValue));
      }
    },
    [measKind, displayKind]
  );

  const loadMeasurements = useCallback(async () => {
    if (!variable?.id || !report?.id) return;
    setIsLoadingHistory(true);
    try {
      const response = await fetchReportVariableMeasurements(report.id, variable.id);
      if (response.data) {
        const sorted = Array.isArray(response.data)
          ? [...response.data].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          : [];
        setMeasurements(sorted);
      }
    } catch (error) {
      console.error(error);
      toast.error(
        t("report.variables.sketchEditor.historyLoadError", { defaultValue: "Kunne ikke laste målinger" })
      );
    } finally {
      setIsLoadingHistory(false);
    }
  }, [variable?.id, report?.id, t]);

  useEffect(() => {
    if (!open || !variable || !typeGroup) return;

    setActiveTab("measurements");
    if (projKind && measKind) {
      setDisplayKind(projKind);
    } else {
      setDisplayKind(projKind || measKind || "m3h");
    }
    setReference(variable.reference || "");
    setNewMeasurementInput("");

    const pm3 =
      variable.projected_quantity != null && String(variable.projected_quantity).trim() !== "" && projKind
        ? storedToM3h(variable.projected_quantity, projKind)
        : null;
    setProjInput(
      pm3 != null
        ? m3hToDisplay(pm3, projKind)
        : variable.projected_quantity != null
          ? String(variable.projected_quantity)
          : ""
    );

    const prem3 =
      variable.pre_measurement_quantity != null &&
      String(variable.pre_measurement_quantity).trim() !== "" &&
      measKind
        ? storedToM3h(variable.pre_measurement_quantity, measKind)
        : null;
    setPreInput(
      prem3 != null
        ? m3hToDisplay(prem3, measKind)
        : variable.pre_measurement_quantity != null
          ? String(variable.pre_measurement_quantity)
          : ""
    );

    const embeddedLatest = variable.measurements?.[0]?.value;
    if (embeddedLatest != null && String(embeddedLatest).trim() !== "") {
      if (measKind) {
        const m3 = storedToM3h(embeddedLatest, measKind);
        setMeasInput(m3 != null ? m3hToDisplay(m3, measKind) : String(embeddedLatest));
      } else {
        setMeasInput(String(embeddedLatest));
      }
    } else {
      setMeasInput("");
    }
  }, [open, variable?.id, typeGroup?.id, projKind, measKind, variable]);

  useEffect(() => {
    if (!open || !variable?.id || !report?.id) {
      setMeasurements([]);
      return;
    }
    loadMeasurements();
  }, [open, variable?.id, report?.id, loadMeasurements]);

  const flipDisplayInputs = (from, to) => {
    if (!showFlowToggle) return;
    const flipField = (s) => {
      const m3 = displayInputToM3h(s, from);
      if (m3 == null) return s;
      return m3hToDisplay(m3, to);
    };
    setProjInput((s) => flipField(s));
    setPreInput((s) => flipField(s));
    setMeasInput((s) => flipField(s));
    setNewMeasurementInput((s) => flipField(s));
    setDisplayKind(to);
  };

  const latestMeasurement = measurements.length > 0 ? measurements[0] : null;

  /** Comparable «målt» for status (m³/h-space when flow, else raw number in måleenhet). */
  const resolvedM3Meas = useMemo(() => {
    if (measKind) {
      const fromField = displayInputToM3h(measInput, displayKind);
      if (fromField != null) return fromField;
      const v = latestMeasurement?.value;
      if (v == null || String(v).trim() === "") return null;
      return storedToM3h(v, measKind);
    }
    const fromField = parseNumericInput(measInput);
    if (fromField != null) return fromField;
    const v = latestMeasurement?.value;
    if (v == null || String(v).trim() === "") return null;
    return parseNumericInput(v);
  }, [measInput, displayKind, latestMeasurement, measKind]);

  const statusBanner = useMemo(() => {
    let projVal;
    let measVal;

    if (showFlowToggle) {
      projVal = displayInputToM3h(projInput, displayKind);
      measVal = resolvedM3Meas;
    } else if (!projKind && !measKind) {
      projVal = parseNumericInput(projInput);
      measVal = parseNumericInput(measInput);
      if (measVal == null && latestMeasurement?.value != null) {
        measVal = parseNumericInput(latestMeasurement.value);
      }
    } else {
      return null;
    }

    if (measVal == null) return null;
    if (projVal == null || projVal === 0) {
      return {
        tone: "ok",
        title: t("report.variables.sketchEditor.statusMeasured", { defaultValue: "Målt" }),
      };
    }
    const dev = Math.abs(measVal - projVal) / Math.max(Math.abs(projVal), 1e-9);
    if (dev <= 0.15) {
      return {
        tone: "ok",
        title: t("report.variables.sketchEditor.statusGood", { defaultValue: "God match" }),
        sub: t("report.variables.sketchEditor.statusGoodSub", {
          defaultValue: "Målt verdi er innenfor akseptabelt område",
        }),
      };
    }
    return {
      tone: "warn",
      title: t("report.variables.sketchEditor.statusCheck", { defaultValue: "Sjekk avvik" }),
      sub: t("report.variables.sketchEditor.statusCheckSub", {
        defaultValue: "Målt sammenlignes mot prosjektert i samme visningsenhet",
      }),
    };
  }, [
    projInput,
    measInput,
    resolvedM3Meas,
    displayKind,
    showFlowToggle,
    projKind,
    measKind,
    latestMeasurement,
    t,
  ]);

  const renderStatusBanner = () => {
    if (!statusBanner) return null;
    return (
      <div
        className={`rounded-2xl border p-4 ${
          statusBanner.tone === "ok"
            ? "border-emerald-100 bg-emerald-50/60"
            : "border-amber-100 bg-amber-50/60"
        }`}
      >
        <span
          className={`font-medium ${
            statusBanner.tone === "ok" ? "text-emerald-900" : "text-amber-900"
          }`}
        >
          {statusBanner.title}
        </span>
        {statusBanner.sub ? (
          <p
            className={`mt-1 text-sm ${
              statusBanner.tone === "ok" ? "text-emerald-800" : "text-amber-800"
            }`}
          >
            {statusBanner.sub}
          </p>
        ) : null}
      </div>
    );
  };

  const displayValueToStored = (raw) => {
    if (measKind) {
      const m3 = displayInputToM3h(raw, displayKind);
      if (m3 != null) return m3hToStored(m3, measKind);
    }
    if (String(raw ?? "").trim() === "") return null;
    const n = parseFloat(String(raw).replace(",", "."));
    return Number.isNaN(n) ? null : n;
  };

  const handleAddMeasurement = async () => {
    if (!variable?.id || !report?.id) return;
    const stored = displayValueToStored(newMeasurementInput);
    if (stored == null || stored <= 0) {
      toast.error(
        t("report.variables.sketchEditor.measurementInvalid", {
          defaultValue: "Oppgi en gyldig målingsverdi",
        })
      );
      return;
    }

    setIsAddingMeasurement(true);
    try {
      const response = await createReportVariableMeasurement(report.id, variable.id, { value: stored });
      if (response.data) {
        toast.success(
          t("report.variables.sketchEditor.measurementAdded", { defaultValue: "Måling lagt til" })
        );
        setNewMeasurementInput("");
        await loadMeasurements();
        syncMeasInputFromStored(stored);
        if (onSaved) await onSaved();
      } else {
        toast.error(
          response.errors?.[0] ||
            t("report.variables.sketchEditor.measureError", { defaultValue: "Kunne ikke lagre måling" })
        );
      }
    } catch (error) {
      if (mutations) return;
      console.error(error);
      toast.error(
        t("report.variables.sketchEditor.measureError", { defaultValue: "Kunne ikke lagre måling" })
      );
    } finally {
      setIsAddingMeasurement(false);
    }
  };

  const handleSaveDetails = async () => {
    if (!variable?.id || !report?.id || !typeGroup) return;
    if (!reference?.trim()) {
      toast.error(t("report.variables.sketchEditor.refRequired", { defaultValue: "Referanse er påkrevd" }));
      return;
    }

    setSaving(true);
    try {
      const m3Proj = projKind ? displayInputToM3h(projInput, displayKind) : parseNumericInput(projInput);
      const m3Pre = measKind ? displayInputToM3h(preInput, displayKind) : parseNumericInput(preInput);
      const m3Meas = measKind ? displayInputToM3h(measInput, displayKind) : parseNumericInput(measInput);

      let projected_quantity = null;
      if (m3Proj != null && projKind) {
        const s = m3hToStored(m3Proj, projKind);
        projected_quantity = s != null ? String(s) : null;
      } else if (String(projInput ?? "").trim() !== "" && !projKind) {
        projected_quantity = String(projInput).replace(",", ".");
      }

      let pre_measurement_quantity = null;
      if (m3Pre != null && measKind) {
        const s = m3hToStored(m3Pre, measKind);
        pre_measurement_quantity = s != null ? String(s) : null;
      } else if (String(preInput ?? "").trim() !== "" && !measKind) {
        pre_measurement_quantity = String(preInput).replace(",", ".");
      }

      const upd = await updateReportVariable(report.id, variable.id, {
        reference: reference.trim(),
        projected_quantity,
        pre_measurement_quantity,
        note: variable.note ?? null,
      });

      if (!upd.data) {
        toast.error(upd.errors?.[0] || t("report.variables.sketchEditor.saveError", { defaultValue: "Kunne ikke lagre" }));
        setSaving(false);
        return;
      }

      const latestStored =
        latestMeasurement?.value != null && String(latestMeasurement.value).trim() !== ""
          ? parseFloat(latestMeasurement.value)
          : null;

      if (m3Meas != null) {
        const storedMeas = measKind ? m3hToStored(m3Meas, measKind) : m3Meas;
        if (
          storedMeas != null &&
          (latestStored == null || Number.isNaN(latestStored) || Math.abs(storedMeas - latestStored) > 1e-6)
        ) {
          const cm = await createReportVariableMeasurement(report.id, variable.id, { value: storedMeas });
          if (!cm.data) {
            toast.error(
              cm.errors?.[0] ||
                t("report.variables.sketchEditor.measureError", { defaultValue: "Kunne ikke lagre måling" })
            );
            setSaving(false);
            return;
          }
          await loadMeasurements();
        }
      }

      toast.success(t("report.variables.sketchEditor.saved", { defaultValue: "Lagret" }));
      if (onSaved) await onSaved();
      onClose();
    } catch (e) {
      if (mutations) return;
      console.error(e);
      toast.error(t("report.variables.sketchEditor.saveError", { defaultValue: "Kunne ikke lagre" }));
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDeleteSubvariable = async () => {
    if (!variable?.id || !report?.id || !isSubVariable) return;
    setDeleting(true);
    try {
      const response = await deleteReportVariable(report.id, variable.id);
      const errList = response?.errors;
      if (Array.isArray(errList) && errList.length > 0) {
        toast.error(
          errList[0] ||
            t("report.variables.sketchEditor.subvariableDeleteError", { defaultValue: "Kunne ikke slette variabel" })
        );
        return;
      }
      if (response?.error && typeof response.error === "string") {
        toast.error(response.error);
        return;
      }
      if (response?.status && response.status >= 400) {
        toast.error(
          t("report.variables.sketchEditor.subvariableDeleteError", { defaultValue: "Kunne ikke slette variabel" })
        );
        return;
      }

      setDeleteConfirmOpen(false);
      toast.success(
        t("report.variables.sketchEditor.subvariableDeleted", { defaultValue: "Undervariabel slettet" })
      );
      if (onSaved) await onSaved();
      onClose();
    } catch (e) {
      if (mutations) return;
      console.error(e);
      toast.error(
        t("report.variables.sketchEditor.subvariableDeleteError", { defaultValue: "Kunne ikke slette variabel" })
      );
    } finally {
      setDeleting(false);
    }
  };

  const dateLocale = intlLocaleFor(i18n.language);

  const tabBtn = (id, label) => (
    <button
      type="button"
      role="tab"
      aria-selected={activeTab === id}
      onClick={() => setActiveTab(id)}
      className={`rounded-xl px-4 py-2 text-sm font-medium transition ${
        activeTab === id ? "bg-white text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-900"
      }`}
    >
      {label}
    </button>
  );

  const unitToggle = (
    <div className="inline-flex rounded-2xl bg-gray-100 p-1">
      <button
        type="button"
        onClick={() => displayKind !== "m3h" && flipDisplayInputs(displayKind, "m3h")}
        className={`rounded-xl px-4 py-2 text-sm font-medium transition ${
          displayKind === "m3h" ? "bg-white text-gray-900 shadow-sm" : "text-gray-600"
        }`}
      >
        m³/h
      </button>
      <button
        type="button"
        onClick={() => displayKind !== "ls" && flipDisplayInputs(displayKind, "ls")}
        className={`rounded-xl px-4 py-2 text-sm font-medium transition ${
          displayKind === "ls" ? "bg-white text-gray-900 shadow-sm" : "text-gray-600"
        }`}
      >
        l/s
      </button>
    </div>
  );

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className={RV_DIALOG_Z} onClose={onClose}>
        <Transition.Child as={Fragment} {...rvTransitionOverlay}>
          <div className={RV_OVERLAY_CLASS} />
        </Transition.Child>

        <div className={RV_CENTER_WRAPPER}>
          <div className={RV_CENTER_INNER}>
            <Transition.Child as={Fragment} {...rvTransitionPanel}>
              <Dialog.Panel className="flex max-h-[min(92dvh,48rem)] w-full max-w-lg transform flex-col rounded-t-3xl border border-gray-200 bg-white shadow-2xl transition-all sm:max-w-xl sm:rounded-3xl !transform-none">
                <div ref={collaborationAnchor} />
                <div className="flex shrink-0 items-start justify-between border-b border-gray-200 px-5 py-4 sm:px-6">
                  <div className="min-w-0 pr-2">
                    <Dialog.Title className="truncate text-lg font-semibold text-gray-900">
                      {reference || variable?.reference || "—"}
                    </Dialog.Title>
                    <p className="truncate text-sm text-gray-500">
                      {group?.title ||
                        t("report.variables.sketchEditor.noGroup", { defaultValue: "Uten gruppe" })}
                    </p>
                  </div>
                  <button
                    type="button"
                    className={RV_CLOSE_BTN}
                    onClick={onClose}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div
                  className="flex shrink-0 gap-1 border-b border-gray-100 px-5 py-3 sm:px-6"
                  role="tablist"
                  aria-label={t("report.variables.sketchEditor.tabsAria", { defaultValue: "Variabel" })}
                >
                  <div className="inline-flex rounded-2xl bg-gray-100 p-1">
                    {tabBtn(
                      "measurements",
                      t("report.variables.sketchEditor.tabMeasurements", { defaultValue: "Målinger" })
                    )}
                    {tabBtn(
                      "details",
                      t("report.variables.sketchEditor.tabDetails", { defaultValue: "Verdier" })
                    )}
                  </div>
                </div>

                <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5 sm:px-6">
                  {activeTab === "measurements" ? (
                    <>
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <p className="text-sm text-gray-600">
                          {t("report.variables.sketchEditor.measurementsIntro", {
                            defaultValue: "Registrer ny måling eller se historikk.",
                          })}
                        </p>
                        {showFlowToggle ? unitToggle : null}
                      </div>

                      <div className="rounded-2xl border border-gray-200 bg-gray-50/80 p-4">
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          {t("report.variables.measurementDialog.projectedLabel", {
                            defaultValue: "Prosjektert",
                          })}
                        </h3>
                        <div className="mt-1 flex flex-wrap items-baseline gap-2">
                          <span className="text-lg font-semibold tabular-nums text-gray-900">
                            {String(projInput ?? "").trim() !== "" ? projInput : "—"}
                          </span>
                          {showFlowToggle ? (
                            <span className="text-sm text-gray-500">{projectedFieldSuffix}</span>
                          ) : projectedFieldSuffix ? (
                            <span className="text-sm text-gray-500">{projectedFieldSuffix}</span>
                          ) : projectedUnit ? (
                            <span className="text-sm text-gray-500">({formatUnitDisplay(projectedUnit)})</span>
                          ) : null}
                        </div>
                      </div>

                      {renderStatusBanner()}

                      {latestMeasurement ? (
                        <div className="rounded-2xl border border-gray-200 bg-gray-50/80 p-4">
                          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                            {t("report.variables.measurementDialog.latestHeading", { defaultValue: "Siste måling" })}
                          </h3>
                          <div className="mt-1 text-xl font-semibold tabular-nums text-gray-900">
                            {measurementUnit
                              ? formatUnitDisplay(measurementUnit, latestMeasurement.value)
                              : parseFloat(latestMeasurement.value).toFixed(2)}
                          </div>
                          <p className="mt-1 text-xs text-gray-500">
                            {formatMeasurementDate(latestMeasurement.created_at, dateLocale)}
                          </p>
                        </div>
                      ) : null}

                      {canEdit ? (
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">
                            {t("report.variables.sketchEditor.addMeasurementHeading", {
                              defaultValue: "Legg til ny måling",
                            })}
                          </label>
                          <div className="flex min-w-0 items-stretch gap-2">
                            <div className="min-w-0 flex-1">
                              <div className="flex min-w-0 gap-2">
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={newMeasurementInput}
                                  onChange={(e) => setNewMeasurementInput(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") handleAddMeasurement();
                                  }}
                                  className={`min-w-0 flex-1 ${RV_INPUT} py-2.5 text-sm`}
                                  placeholder={t("report.variables.sketchEditor.addMeasurementPlaceholder", {
                                    defaultValue: "Verdi",
                                  })}
                                />
                                <span className="flex w-11 shrink-0 items-center justify-end text-xs text-gray-400 sm:w-12">
                                  {measurementFieldSuffix}
                                </span>
                              </div>
                              {measurementUnit &&
                              newMeasurementInput &&
                              !Number.isNaN(parseFloat(String(newMeasurementInput).replace(",", "."))) ? (
                                <p className="mt-1 text-xs text-gray-500">
                                  {formatUnitDisplay(
                                    measurementUnit,
                                    displayValueToStored(newMeasurementInput)
                                  )}
                                </p>
                              ) : null}
                            </div>
                            <button
                              type="button"
                              className={RV_BTN_OUTLINE_SM}
                              disabled={isAddingMeasurement || !String(newMeasurementInput).trim()}
                              onClick={handleAddMeasurement}
                            >
                              {isAddingMeasurement
                                ? t("report.variables.sketchEditor.addingMeasurement", { defaultValue: "Legger til…" })
                                : t("report.variables.sketchEditor.addMeasurementButton", { defaultValue: "Legg til" })}
                            </button>
                          </div>
                        </div>
                      ) : null}

                      <div>
                        <h3 className="mb-2 text-sm font-medium text-gray-900">
                          {t("report.variables.sketchEditor.historyHeading", {
                            defaultValue: "Målingshistorikk ({{count}})",
                            count: measurements.length,
                          })}
                        </h3>
                        {isLoadingHistory ? (
                          <p className="py-6 text-center text-sm text-gray-500">
                            {t("report.variables.sketchEditor.historyLoading", { defaultValue: "Laster målinger…" })}
                          </p>
                        ) : measurements.length === 0 ? (
                          <p className="rounded-2xl border border-dashed border-gray-200 py-6 text-center text-sm text-gray-500">
                            {t("report.variables.sketchEditor.historyEmpty", {
                              defaultValue: "Ingen målinger registrert",
                            })}
                          </p>
                        ) : (
                          <div className="max-h-48 overflow-y-auto rounded-2xl border border-gray-200">
                            <table className="min-w-full divide-y divide-gray-100 text-sm">
                              <thead className="sticky top-0 bg-gray-50">
                                <tr>
                                  <th className="px-3 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                                    {t("report.variables.sketchEditor.historyColValue", { defaultValue: "Verdi" })}
                                  </th>
                                  <th className="px-3 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                                    {t("report.variables.sketchEditor.historyColDate", { defaultValue: "Dato/tid" })}
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100 bg-white">
                                {measurements.map((m, idx) => (
                                  <tr
                                    key={m.id ?? `${m.created_at}-${idx}`}
                                    className={idx === 0 ? "bg-gray-50/80" : "hover:bg-gray-50"}
                                  >
                                    <td className="whitespace-nowrap px-3 py-2.5 font-medium text-gray-900">
                                      {measurementUnit
                                        ? formatUnitDisplay(measurementUnit, m.value)
                                        : parseFloat(m.value).toFixed(2)}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-2.5 text-gray-500">
                                      {formatMeasurementDate(m.created_at, dateLocale)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>

                      {canEdit ? (
                        <p className="text-xs text-gray-500">
                          <button
                            type="button"
                            className="font-medium text-gray-800 underline decoration-gray-300 underline-offset-2 hover:decoration-gray-800"
                            onClick={() => setActiveTab("details")}
                          >
                            {t("report.variables.sketchEditor.goToDetails", {
                              defaultValue: "Rediger referanse og øvrige verdier →",
                            })}
                          </button>
                        </p>
                      ) : null}
                    </>
                  ) : (
                    <>
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <p className="text-sm text-gray-600">
                          {t("report.variables.sketchEditor.detailsIntro", {
                            defaultValue: "Referanse og felt som lagres sammen.",
                          })}
                        </p>
                        {showFlowToggle ? unitToggle : null}
                      </div>

                      {canEdit ? (
                        <div>
                          <label className="mb-1.5 block text-sm font-medium text-gray-700">
                            {t("report.variables.measurementDialog.reference", { defaultValue: "Referanse" })}
                          </label>
                          <input
                            type="text"
                            value={reference}
                            onChange={(e) => setReference(e.target.value)}
                            className={`w-full ${RV_INPUT}`}
                          />
                        </div>
                      ) : (
                        <p className="text-sm text-gray-700">
                          <span className="font-medium text-gray-600">
                            {t("report.variables.measurementDialog.reference", { defaultValue: "Referanse" })}:
                          </span>{" "}
                          {reference || "—"}
                        </p>
                      )}

                      <div>
                        <label className="mb-1.5 block text-sm font-medium text-gray-700">
                          {t("report.variables.measurementDialog.projectedLabel", { defaultValue: "Prosjektert" })}
                        </label>
                        <div className="flex min-w-0 gap-2">
                          <input
                            type="text"
                            inputMode="decimal"
                            disabled={!canEdit}
                            value={projInput}
                            onChange={(e) => setProjInput(e.target.value)}
                            className={`min-w-0 flex-1 ${RV_INPUT} disabled:opacity-60`}
                          />
                          <span className="flex w-11 shrink-0 items-center text-xs text-gray-400 sm:w-12">
                            {projectedFieldSuffix}
                          </span>
                        </div>
                      </div>

                      <div>
                        <label className="mb-1.5 block text-sm font-medium text-gray-700">
                          {t("report.variables.measurementDialog.preMeasurementLabel", { defaultValue: "Før-måling" })}
                        </label>
                        <div className="flex min-w-0 gap-2">
                          <input
                            type="text"
                            inputMode="decimal"
                            disabled={!canEdit}
                            value={preInput}
                            onChange={(e) => setPreInput(e.target.value)}
                            className={`min-w-0 flex-1 ${RV_INPUT} disabled:opacity-60`}
                          />
                          <span className="flex w-11 shrink-0 items-center text-xs text-gray-400 sm:w-12">
                            {measurementFieldSuffix}
                          </span>
                        </div>
                      </div>

                      <div>
                        <label className="mb-1.5 block text-sm font-medium text-gray-700">
                          {t("report.variables.sketchEditor.measuredActual", { defaultValue: "Målt (faktisk)" })}
                        </label>
                        <p className="mb-2 text-xs text-gray-500">
                          {t("report.variables.sketchEditor.measuredHintDetails", {
                            defaultValue:
                              "Oppdaterer siste måling hvis du lagrer en ny verdi her. Nye målepunkter legger du til under Målinger.",
                          })}
                        </p>
                        <div className="flex min-w-0 gap-2">
                          <input
                            type="text"
                            inputMode="decimal"
                            disabled={!canEdit}
                            value={measInput}
                            onChange={(e) => setMeasInput(e.target.value)}
                            className={`min-w-0 flex-1 ${RV_INPUT_MEASURED} disabled:opacity-60`}
                          />
                          <span className="flex w-11 shrink-0 items-center text-xs text-gray-400 sm:w-12">
                            {measurementFieldSuffix}
                          </span>
                        </div>
                      </div>

                      {renderStatusBanner()}

                      {canEdit && isSubVariable ? (
                        <div className="rounded-2xl border border-red-100 bg-red-50/50 px-4 py-3">
                          {!deleteConfirmOpen ? (
                            <>
                              <p className="text-xs text-red-900/80">
                                {t("report.variables.sketchEditor.deleteSubvariableHint", {
                                  defaultValue:
                                    "Sletting gjelder kun denne undervariabelen. Hovedvariabel og type-gruppe beholdes.",
                                })}
                              </p>
                              <button
                                type="button"
                                className="mt-2 inline-flex items-center gap-2 rounded-xl border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-700 shadow-sm transition hover:bg-red-50"
                                onClick={() => setDeleteConfirmOpen(true)}
                              >
                                <TrashIcon className="h-5 w-5 shrink-0" aria-hidden />
                                {t("report.variables.sketchEditor.deleteSubvariable", {
                                  defaultValue: "Slett undervariabel",
                                })}
                              </button>
                            </>
                          ) : (
                            <div className="space-y-3">
                              <p className="text-sm font-medium text-red-900">
                                {t("report.variables.sketchEditor.deleteSubvariableConfirm", {
                                  reference: reference || variable?.reference || "—",
                                })}
                              </p>
                              <div className="flex flex-wrap gap-2">
                                <button
                                  type="button"
                                  className={`${RV_BTN_OUTLINE} !min-h-10`}
                                  disabled={deleting}
                                  onClick={() => setDeleteConfirmOpen(false)}
                                >
                                  {t("report.variables.sketchEditor.deleteSubvariableCancel", {
                                    defaultValue: "Avbryt",
                                  })}
                                </button>
                                <button
                                  type="button"
                                  className="inline-flex min-h-10 items-center justify-center rounded-xl border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
                                  disabled={deleting}
                                  onClick={() => {
                                    void handleConfirmDeleteSubvariable();
                                  }}
                                >
                                  {deleting
                                    ? t("report.variables.sketchEditor.deleteSubvariableWorking", {
                                        defaultValue: "Sletter…",
                                      })
                                    : t("report.variables.sketchEditor.deleteSubvariableConfirmAction", {
                                        defaultValue: "Slett permanent",
                                      })}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : null}
                    </>
                  )}
                </div>

                <div className="flex shrink-0 flex-col gap-2 border-t border-gray-200 p-4 sm:flex-row sm:justify-end sm:gap-3">
                  {activeTab === "measurements" ? (
                    <button
                      type="button"
                      className={`w-full ${RV_BTN_OUTLINE} sm:ml-auto sm:w-auto`}
                      onClick={onClose}
                    >
                      {t("report.variables.sketchEditor.close", { defaultValue: "Lukk" })}
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        className={`w-full ${RV_BTN_OUTLINE} sm:w-auto`}
                        onClick={onClose}
                      >
                        {t("report.variables.sketchEditor.cancel", { defaultValue: "Avbryt" })}
                      </button>
                      {canEdit ? (
                        <button
                          type="button"
                          className={`w-full ${RV_BTN_PRIMARY_DARK} sm:w-auto`}
                          disabled={saving}
                          onClick={handleSaveDetails}
                        >
                          {saving
                            ? t("report.variables.sketchEditor.saving", { defaultValue: "Lagrer…" })
                            : t("report.variables.sketchEditor.saveChanges", { defaultValue: "Lagre endringer" })}
                        </button>
                      ) : null}
                    </>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default VariableSketchEditorDialog;
